// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { activityLabel, OUTCOME_COLOUR, OUTCOME_LABEL, OUTCOME_ORDER } from '../domain';
import { activityStats, outcomeCounts } from '../analysis';
import { horizontalBars, stackedBarHtml, type DonutSeg } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatNumber, formatPercent } from '../utils/format';

export function renderActivities(root: HTMLElement, ctx: ViewContext): void {
  const acts = activityStats(ctx.data.incidents).filter((a) => a.total >= 3);
  const byCount = [...acts].sort((a, b) => b.total - a.total);
  const byFatal = [...acts].filter((a) => a.fatal + a.total > 0).sort((a, b) => b.fatalPct - a.fatalPct);
  const byProvoked = [...acts].filter((a) => a.provoked + a.unprovoked >= 5).sort((a, b) => b.provokedPct - a.provokedPct);

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">What people were doing</h1>
      <p class="view-sub">
        The activity at the moment of the bite shapes the odds. Swimmers are bitten most and fare worst; board-riders are
        bitten nearly as often but survive far more. And a whole class of incidents is
        ${glossaryTerm('provoked', 'provoked')} — people fishing, spearfishing or handling a shark.
      </p>
    </div>

    <div class="panel">
      <div class="panel-title">Every activity, ranked</div>
      <div class="panel-sub">Incidents, fatal rate, provoked share and the full outcome split. Hover a bar segment for exact numbers.</div>
      <div class="table-scroll">
        <table class="data">
          <thead><tr><th>Activity</th><th class="t-num">Incidents</th><th class="t-num">Fatal</th><th class="t-num">Fatal rate</th><th class="t-num">Provoked</th><th>Outcome split</th></tr></thead>
          <tbody>
            ${byCount
              .map((a) => {
                const sub = ctx.data.incidents.filter((i) => i.act === a.key);
                const c = outcomeCounts(sub);
                const segs: DonutSeg[] = OUTCOME_ORDER.map((o) => ({ value: c[o], colour: OUTCOME_COLOUR[o], label: OUTCOME_LABEL[o] }));
                return `<tr style="cursor:default">
                  <td><strong>${escapeHtml(activityLabel(a.key))}</strong></td>
                  <td class="t-num">${formatNumber(a.total)}</td>
                  <td class="t-num">${formatNumber(a.fatal)}</td>
                  <td class="t-num" style="color:${a.fatalPct >= 25 ? OUTCOME_COLOUR.fatal : 'inherit'}">${formatPercent(a.fatalPct, 0)}</td>
                  <td class="t-num">${formatPercent(a.provokedPct, 0)}</td>
                  <td style="min-width:180px">${stackedBarHtml(segs)}</td>
                </tr>`;
              })
              .join('')}
          </tbody>
        </table>
      </div>
      <div class="legend" style="margin-top:var(--space-md)">${OUTCOME_ORDER.map((o) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OUTCOME_COLOUR[o]}"></span>${OUTCOME_LABEL[o]}</span>`).join('')}</div>
    </div>

    <div class="grid-2" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-title">Fatal rate by activity</div>
        <div class="panel-sub">Share of each activity's incidents that proved fatal. Swimming leads; board-riding is much safer.</div>
        <div class="chart-scroll" data-role="fatal"></div>
      </div>
      <div class="panel">
        <div class="panel-title">How often the bite was provoked</div>
        <div class="panel-sub">Fishing, spearfishing and handling top the list — these are rarely "attacks" in the everyday sense.</div>
        <div class="chart-scroll" data-role="prov"></div>
      </div>
    </div>
  `;

  (root.querySelector('[data-role="fatal"]') as HTMLElement).appendChild(
    horizontalBars(
      byFatal.map((a) => ({
        id: a.key,
        label: activityLabel(a.key),
        value: a.fatalPct,
        colour: OUTCOME_COLOUR.fatal,
        tip: `${activityLabel(a.key)}: ${formatPercent(a.fatalPct, 0)} fatal (${formatNumber(a.fatal)} of ${formatNumber(a.total)})`,
      })),
      { labelWidth: 130, valueFormat: (v) => `${v.toFixed(0)}%` },
    ),
  );

  (root.querySelector('[data-role="prov"]') as HTMLElement).appendChild(
    horizontalBars(
      byProvoked.map((a) => ({
        id: a.key,
        label: activityLabel(a.key),
        value: a.provokedPct,
        colour: OUTCOME_COLOUR.injured,
        tip: `${activityLabel(a.key)}: ${formatPercent(a.provokedPct, 0)} provoked (${formatNumber(a.provoked)} of ${formatNumber(a.provoked + a.unprovoked)})`,
      })),
      { labelWidth: 130, valueFormat: (v) => `${v.toFixed(0)}%` },
    ),
  );
}
